import type { ReactNode } from "react";
import type { Project, ProjectOverview } from "@codvero/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Code2, MessageSquare, Play, Plus, Search, Server, Settings2, Square } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { overviewApi, projectsApi } from "@/api/projects.ts";
import { useProjectActions } from "@/features/projects/ProjectPage.tsx";
import { useOpenIde } from "@/features/projects/useOpenIde.ts";
import { useApiError } from "@/ui/useApiError.ts";
import { cn } from "@/lib/cn.ts";
import { cpuShare } from "@/lib/cpu.ts";
import { Button, Input } from "@/ui/primitives.tsx";

const uptime = (iso: string | null, t: TFunction) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return t("machines.up", { time: h ? `${h}h ${m}m` : `${m}m` });
};
const gb = (mb: number | null) => (mb === null ? "—" : (mb / 1024).toFixed(1));

/** A machine before its live stats arrive: the card shows it, with dashes for the numbers. */
const pending = (project: Project): ProjectOverview => ({ project, startedAt: null, cpuPercent: null, memoryUsedMb: null, hostMemoryMb: null, memoryLimitMb: null, cpuCores: null, ports: [], chatCount: 0, lastChat: null });

/** The "Machines" grid from the original: live stats, ports, last chat, Chat/IDE/stop per project. */
/** `above`: shown between the header bar and the cards (the home page's new-conversation box). */
export function MachinesGrid({ onNew, above }: { onNew: () => void; above?: ReactNode }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["projects", "overview"], queryFn: overviewApi.list, refetchInterval: 5000 });
  // The overview waits for every running container's live stats and open ports (seconds); the plain list is quick and
  // usually cached already (the sidebar). Show the machines from it at once; the stats fill in when they arrive.
  const plain = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, enabled: !q.data });
  const data = q.data ?? plain.data?.map(pending);
  const [search, setSearch] = useState("");
  const rows = (data ?? []).filter((o) => !search || o.project.name.toLowerCase().includes(search.toLowerCase()) || o.project.slug.includes(search.toLowerCase()));
  const live = (data ?? []).filter((o) => o.project.state === "running").length;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("machines.title")}</h1>
          <div dir="ltr" className="mt-1 font-mono text-xs text-fg-muted">{t("machines.count", { projects: data?.length ?? 0 })} · <span className="text-accent">{t("machines.live", { live })}</span></div>
        </div>
        {/* phone: a full-width row under the title; wider screens: beside it */}
        <div className="flex w-full items-center gap-2 md:ms-auto md:w-auto">
          <div className="relative min-w-0 flex-1 md:flex-none">
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" />
            <Input data-global-search className="h-10 w-full ps-9 md:w-64" placeholder={t("machines.find")} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Button className="size-10 p-0" aria-label={t("projects.new")} onClick={onNew}><Plus className="size-5" /></Button>
        </div>
      </div>
      <div className="border-t border-border" />
      {above}
      <div className="@container">
      <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
        {rows.map((o) => <MachineCard key={o.project.id} o={o} />)}
        <button type="button" onClick={onNew} className="grid min-h-64 place-items-center rounded-xl border border-dashed border-border p-4 text-center hover:border-accent/60">
          <div><Server className="mx-auto mb-3 size-6 text-fg-muted" /><div className="font-semibold">{t("projects.new")}</div><div className="mt-1 text-xs text-fg-muted">{t("home.newDesc")}</div></div>
        </button>
      </div>
      </div>
    </div>
  );
}

function MachineCard({ o }: { o: ProjectOverview }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const p = o.project;
  const actions = useProjectActions(p.id);
  const ide = useOpenIde(p.id);
  const toMsg = useApiError();
  const running = p.state === "running";
  // share of what this container may use (its limits, or the whole server when unlimited), not of one core / the host
  const cpu = cpuShare(o.cpuPercent, o.cpuCores);
  const memLimit = o.memoryLimitMb ?? o.hostMemoryMb;
  const memPct = o.memoryUsedMb && memLimit ? Math.min(100, (o.memoryUsedMb / memLimit) * 100) : 0;
  const rel = (iso: string) => new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" }).format(-Math.round((Date.now() - new Date(iso).getTime()) / 86400000), "day");
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("size-2 rounded-full", running ? "bg-accent" : p.state === "error" ? "bg-danger" : "bg-fg-muted/50")} />
            <Link to="/projects/$projectId" params={{ projectId: p.id }} className="truncate text-base font-semibold hover:underline">{p.name}</Link>
          </div>
          <div dir="ltr" className="ms-4 mt-0.5 font-mono text-[11px] uppercase tracking-wide text-fg-muted">{t(`projects.state.${p.state}`)}{running && o.startedAt ? ` · ${uptime(o.startedAt, t)}` : ""}</div>
        </div>
        <Link to="/projects/$projectId/settings" params={{ projectId: p.id }} aria-label={t("projects.tabs.settings")} className="rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg"><Settings2 className="size-4" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat label={t("machines.cpu")} value={running && cpu ? `${cpu.pct}%${cpu.cores ? ` · ${cpu.used}/${cpu.cores}` : ""}` : "—"} pct={cpu?.pct ?? 0} title={cpu?.cores ? t("machines.cpuHint", { used: cpu.used, cores: cpu.cores }) : undefined} />
        <Stat label={t("machines.memory")} value={running ? `${gb(o.memoryUsedMb)}/${gb(memLimit)} GB` : "—"} pct={memPct} />
      </div>
      <div className="flex min-h-7 flex-wrap gap-1.5" dir="ltr">
        {o.ports.map((port) => <span key={port} className="rounded-md border border-accent/40 bg-accent-soft/40 px-2 py-0.5 font-mono text-xs text-accent">:{port}</span>)}
        {running && o.ports.length === 0 && <span className="text-xs text-fg-muted">{t("machines.noPorts")}</span>}
      </div>
      <button type="button" onClick={() => void nav(o.lastChat ? { to: "/projects/$projectId/chat/$chatId", params: { projectId: p.id, chatId: o.lastChat.id } } : { to: "/projects/$projectId/chat", params: { projectId: p.id } })} className="flex items-center gap-2 rounded-lg border border-border bg-bg px-3 py-2 text-start text-sm hover:border-accent/50">
        <MessageSquare className="size-4 shrink-0 text-fg-muted" />
        <span className="min-w-0 flex-1 truncate">{o.lastChat ? o.lastChat.title : t("chat.new")}</span>
        {o.chatCount > 1 && <span className="shrink-0 text-xs text-fg-muted">+{o.chatCount - 1}</span>}
        {o.lastChat && <span className="shrink-0 text-[11px] text-fg-muted">{rel(o.lastChat.updatedAt)}</span>}
      </button>
      {p.state === "error" && p.stateMessage && <div dir="ltr" className="rounded-md border border-danger/40 bg-danger-soft px-3 py-2 text-start font-mono text-[11px] text-danger">{p.stateMessage}</div>}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <Button variant="secondary" className="min-w-0 gap-1.5 px-2" onClick={() => void nav({ to: "/projects/$projectId/chat", params: { projectId: p.id } })}><MessageSquare className="size-4 shrink-0" /> <span className="truncate">{t("projects.tabs.chat")}</span></Button>
        <Button variant="secondary" disabled={!running || p.myRole === "viewer"} loading={ide.isPending} title={ide.error ? toMsg(ide.error) : t("dock.ide")} className="min-w-0 gap-1.5 px-2" onClick={() => ide.mutate()}><Code2 className="size-4 shrink-0" /> <span className="truncate">IDE</span></Button>
        {p.myRole !== "viewer" && (running ? (
          <Button variant="secondary" className="px-3" loading={actions.stop.isPending} onClick={() => actions.stop.mutate()} aria-label={t("projects.stop")}><Square className="size-4" /></Button>
        ) : (
          <Button className="px-3" loading={actions.start.isPending || p.state === "creating"} onClick={() => actions.start.mutate()} aria-label={t("projects.start")}><Play className="size-4" /></Button>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, pct, title }: { label: string; value: string; pct: number; title?: string | undefined }) {
  return (
    <div title={title}>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
      <div dir="ltr" className="mt-0.5 truncate whitespace-nowrap font-mono text-sm">{value}</div>
      <div className="mt-1.5 h-0.5 rounded bg-border"><div className="h-0.5 rounded bg-accent" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
    </div>
  );
}
